import LocationActions from 'actions/Location'
import NotificationActions from 'actions/Notification'
import ReportActions, { IReportProps } from 'actions/Report'
import SectorActions from 'actions/Sector'
import VariableActions from 'actions/Variable'
import { CustomDatePicker } from 'components/Datepicker'
import { InputSelect } from 'components/InputSelect'
import { ROUTES_NAMES } from 'constants/name.routes'
import { notificationStatus } from 'constants/notificationStatus'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Dispatch } from 'redux'
import { AuthenticationReducer, IAuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { LocationAction, LocationReducer } from 'types/location'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { ReportAction } from 'types/report'
import { SectorAction, SectorReducer } from 'types/sector'
import { VariableAction, VariableReducer } from 'types/variable'
import { ExportButton, FilterButton, FilterMainContainer, FilterSection, FilterTitle, HrCustomLine, disabledExport } from '../styles'
import { AiOutlineDownload } from 'react-icons/ai'
import { REDUX } from 'constants/'
import { multipleNotification } from 'utils/multipleNotification'
import CheckboxList from 'components/CheckboxList'
import { DAMAGE_LEVEL_LIST } from 'utils/damage'

interface LeftSideFiltersProps {
  dashboards?: string[],
  setTooltipAlert?: React.Dispatch<React.SetStateAction<boolean>>
  setDashboards?: React.Dispatch<React.SetStateAction<string[]>>
}
const LeftSideFilters: React.FC<LeftSideFiltersProps> = ({
  setTooltipAlert,
  dashboards,
  setDashboards,
}) => {
  const { pathname } = useLocation()
  const [loading, setLoading] = useState<boolean>(false)
  const [imageRisk, _setImageRisk] = useState<{ value: string, label: string }[]>([{ value: "u_image_risk_yes", label: "Sim" }, { value: "u_image_risk_no", label: "Não" }])
  const currentDate = moment();
  const { control, setValue, getValues, watch, reset } = useForm()
  const [minDate, setMinDate] = useState(currentDate.subtract(1, 'months').toDate())
  const [maxDate, setMaxDate] = useState(new Date())
  const [canExport, setCanExport] = useState(true)
  const [userLocation, setUserLocation] = useState<string[]>([])
  const [userSectors, setUserSectors] = useState<string[]>([])
  // Mapeamento de nome do setor para array de IDs (para agrupar setores com mesmo nome)
  const [sectorNameToIdsMap, setSectorNameToIdsMap] = useState<Record<string, string[]>>({})
  // Ref para evitar chamadas duplicadas no carregamento inicial
  const hasInitialLoadRun = useRef(false)
  // Ref para evitar que o useEffect de dashboards execute no carregamento inicial
  const isInitialMount = useRef(true)
  // Ref para evitar chamadas simultâneas de dispatchAll
  const isDispatchAllRunning = useRef(false)
  // Ref para rastrear o timeout e poder cancelá-lo
  const initialLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Ref para rastrear se dispatchAll já foi chamado após userLocation ser populado
  const hasCalledDispatchAllAfterLocationLoad = useRef(false)
  
  const getMaxAllowedDate = (startDate: Date): Date => {
    return moment(startDate).add(12, 'months').toDate();
  }
  
  let finalDate = watch('date_final')
  let initialDate = watch('date_incident')
  let unitySelected = watch('unity')
  let statusSelected = watch('u_state')

  const notificationDispatch: Dispatch<NotificationAction> = useDispatch()
  const variableDispatch: Dispatch<VariableAction> =  useDispatch()
  const sectorDispatch: Dispatch<SectorAction> = useDispatch()
  const locationDispatch: Dispatch<LocationAction> = useDispatch()
  const reportDispatch: Dispatch<ReportAction> = useDispatch()

  const sectorSelector: SectorReducer = useSelector((state: RootReducerState) => state.sectorReducers)
  const locationSelector: LocationReducer = useSelector((state: RootReducerState) => state.locationReducers)
  const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
  const variableSelector: VariableReducer = useSelector((state: RootReducerState) => state.variableReducers)
  const authenticationSelector: AuthenticationReducer = useSelector((state: RootReducerState) => state.authenticationReducers)

  // Ao entrar na aba Exportar relatório, limpa u_location para o placeholder do select aparecer
  useEffect(() => {
    if (pathname === ROUTES_NAMES.exportReport) {
      setValue('u_location', '')
    }
  }, [pathname, setValue])

  useEffect(() => {
    //console.log("Running useEffect for initialDate and finalDate changes")
    if (pathname === ROUTES_NAMES.exportReport) {
      let plus30 = new Date(initialDate?.getTime() + (30 * 24 * 60 * 60 * 1000))
      if (plus30?.getTime() < finalDate?.getTime()) {
        setValue('date_final', plus30)
      }
    }

    if (initialDate) {
      const maxAllowedDate = getMaxAllowedDate(initialDate);
      setMaxDate(maxAllowedDate);
      
      // Se a data final selecionada for maior que o máximo permitido, ajusta automaticamente
      if (finalDate && finalDate > maxAllowedDate) {
        setValue('date_final', maxAllowedDate);
      }
    }

    setMinDate(initialDate)
  }, [initialDate, finalDate])


  // useEffect para inicializar userLocation quando authenticationSelector estiver disponível
  useEffect(() => {
    if (!authenticationSelector?.dataJwt) {
      return
    }

    const profile = authenticationSelector?.dataJwt?.profile;
    let initialLocations: any[] = [];
    
    if (['nsp', 'location_qa', 'unit_general_manager', 'location_manager'].includes(profile)) {
      initialLocations = authenticationSelector?.dataJwt?.locations || [];
    } else {
      // Fallback: usar locationSelector se disponível, senão usar authenticationSelector
      initialLocations = locationSelector?.locationsList || authenticationSelector?.dataJwt?.locations || [];
    }
    
    const initialLocationIds = initialLocations.map((l) => l.sys_id);
    
    // Só atualizar se ainda não foi setado ou se mudou
    if (userLocation.length === 0 && initialLocationIds.length > 0) {
      setUserLocation(initialLocationIds)
    }
  }, [authenticationSelector?.dataJwt, locationSelector?.locationsList])

  // useEffect para chamar dispatchAll quando userLocation for populado e ainda não foi chamado
  useEffect(() => {
    // Só executar se:
    // 1. userLocation está populado
    // 2. authenticationSelector está disponível
    // 3. hasInitialLoadRun foi setado (useEffect inicial já executou)
    // 4. dispatchAll ainda não foi chamado após location ser carregada
    // 5. dispatchAll não está rodando
    if (
      userLocation && 
      userLocation.length > 0 && 
      authenticationSelector?.dataJwt &&
      hasInitialLoadRun.current &&
      !hasCalledDispatchAllAfterLocationLoad.current &&
      !isDispatchAllRunning.current &&
      pathname === ROUTES_NAMES.reports
    ) {
      hasCalledDispatchAllAfterLocationLoad.current = true
      
      // Pequeno delay para garantir que tudo está pronto
      setTimeout(() => {
        if (!isDispatchAllRunning.current) {
          dispatchAll()
        }
      }, 100)
    }
  }, [userLocation, authenticationSelector?.dataJwt, pathname])

  // useEffect para atualizar setores quando sectorsGroupedByLocationId for carregado
  useEffect(() => {
    if (!userLocation || userLocation.length === 0) {
      return
    }

    if (!sectorSelector?.sectorsGroupedByLocationId) {
      return
    }

    let allSectors: any[] = []

    userLocation.forEach((locationId) => {
      const sectorsForUnity = sectorSelector.sectorsGroupedByLocationId[locationId]
      if (sectorsForUnity && Array.isArray(sectorsForUnity) && sectorsForUnity.length > 0) {
        sectorsForUnity.forEach((sector) => {
          // Evitar duplicados por sys_id
          if (!allSectors.find(s => s.sys_id === sector.sys_id)) {
            allSectors.push({
              u_name: sector.u_name?.trim(),
              sys_id: sector.sys_id,
            })
          }
        })
      }
    })

    if (allSectors.length > 0) {
      SectorActions.setSectors(sectorDispatch, allSectors)
    }
  }, [sectorSelector?.sectorsGroupedByLocationId, userLocation, sectorDispatch])

  useEffect(() => {
    // Evitar execução duplicada (React StrictMode em desenvolvimento)
    if (hasInitialLoadRun.current) {
      return
    }
    
    // Limpar timeout anterior se existir (proteção extra)
    if (initialLoadTimeoutRef.current) {
      clearTimeout(initialLoadTimeoutRef.current)
      initialLoadTimeoutRef.current = null
    }

    setLoading(true)
    VariableActions.getAllVariables(variableDispatch)
    LocationActions.getAllLocations(locationDispatch, 'all')
    // Força recarregamento dos setores limpando o cache primeiro
    SectorActions.clearAllSectors(sectorDispatch)
    SectorActions.getAllSectors(sectorDispatch)
    SectorActions.getAllSectorsGroupedByLocationsId(sectorDispatch)
    setValue('u_state', notificationStatus?.map(option => (option.value)))
    setValue('sentinel_event', sentinelEventMemoSelect?.map(option => (option.value)))

    // Setar flag ANTES de criar o timeout para evitar condições de corrida
    hasInitialLoadRun.current = true
    
    initialLoadTimeoutRef.current = setTimeout(() => {
      // Verificar se authenticationSelector está disponível antes de executar
      if (!authenticationSelector?.dataJwt) {
        setLoading(false)
        initialLoadTimeoutRef.current = null
        // Não marcar como chamado, deixar o useEffect de userLocation chamar quando estiver pronto
        return
      }

      // Verificar se userLocation está populado antes de executar
      // Usar authenticationSelector diretamente para evitar problema de closure
      const profile = authenticationSelector?.dataJwt?.profile;
      let checkLocations: any[] = [];
      
      if (['nsp', 'location_qa', 'unit_general_manager', 'location_manager'].includes(profile)) {
        checkLocations = authenticationSelector?.dataJwt?.locations || [];
      } else {
        checkLocations = locationSelector?.locationsList || authenticationSelector?.dataJwt?.locations || [];
      }
      
        if (!checkLocations || checkLocations.length === 0) {
          setLoading(false)
        initialLoadTimeoutRef.current = null
        // Não marcar como chamado, deixar o useEffect de userLocation chamar quando estiver pronto
        return
      }

      // Verificar novamente antes de executar (proteção extra)
      // Verificar se ainda é o timeout válido (não foi cancelado) e se não está rodando
      if (initialLoadTimeoutRef.current && !isDispatchAllRunning.current && !hasCalledDispatchAllAfterLocationLoad.current) {
        hasCalledDispatchAllAfterLocationLoad.current = true
        dispatchAll()
        setLoading(false)
        initialLoadTimeoutRef.current = null
      } else {
        setLoading(false)
      }
    }, 3000);

    // Cleanup: limpar timeout se o componente for desmontado
    return () => {
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current)
        initialLoadTimeoutRef.current = null
      }
    }
  }, [])

  const dispatchAll = (reloadPrimaryDashboard:boolean = true) => {
    // Evitar chamadas simultâneas
    if (isDispatchAllRunning.current) {
      return
    }
    
    if (pathname === ROUTES_NAMES.reports && authenticationSelector?.dataJwt) {
      isDispatchAllRunning.current = true

      const uUserTypeValue = getValues('u_user_type')
      
      // Garantir que estamos usando o valor mais recente de userLocation
      // Se userLocation estiver vazio, tentar obter das locations disponíveis
      let finalUserLocation = userLocation
      if (!finalUserLocation || finalUserLocation.length === 0) {
        const profile = authenticationSelector?.dataJwt?.profile;
        if (['nsp', 'location_qa', 'unit_general_manager', 'location_manager'].includes(profile)) {
          finalUserLocation = authenticationSelector?.dataJwt?.locations?.map((l: any) => l.sys_id) || []
        } else {
          finalUserLocation = locationSelector?.locationsList?.map((l: any) => l.sys_id) || []
        }
      }
      
      const locationsString = finalUserLocation?.join(';') || null
      const reporterSectorString = userSectors?.join(';') || null
      
      const mainObject: IReportProps = {
        dispatch: reportDispatch,
        startDate: getValues('date_incident')?.toISOString().slice(0, 10),
        finalDate: getValues('date_final')?.toISOString().slice(0, 10),
        locations: locationsString, // Usar finalUserLocation convertido para string
        damageLevel: getValues('damage_level')?.join(';') || null,
        reporterSector: reporterSectorString,
        omsClassification: getValues('u_oms_classification')?.join(';') || null,
        incidentType: getValues('incident_type')?.join(';') || null,
        status: getValues('u_state')?.join(';') || null,
        sentinelEvent: getValues('sentinel_event')?.join(';') || null,
        imageRisk: getValues('image_risk')?.join(';') || null,
        multipleFactor: getValues('multipleType') || multipleNotification[0].id,
        dashboards: getValues('u_dashboards')?.join(';') || null, 
        sectorActive: getValues('sectorActive') || null,
        uUserType: uUserTypeValue && uUserTypeValue !== null ? uUserTypeValue : null
      }

      const secondaryDashboard = async () => {
        try {
          const { dashboards } = mainObject;

          const dashboardsSplited = dashboards?.split(';');
          if (Array.isArray(dashboardsSplited) && dashboardsSplited.length > 0) {
            for (const dashboard of dashboardsSplited) {
              switch (dashboard) {
                case 'notificationsPerMonthCount':
                  console.log('Carregando dashboard Total notificações')
                  await ReportActions.getMonthReport(mainObject);
                  break;
                case 'mostReclassifiedIncidentType':
                  console.log('Carregando dashboard Top 10 incidentes')
                  const incidentObject = { ...mainObject, helperList: variableSelector?.variablesListIncidentType }
                  await ReportActions.getTopIncidentTypeReport(incidentObject);
                  break;
                case 'notificationsPerOmsCount':
                  console.log('Carregando dashboard Classificação OMS')
                  const omsObject = { ...mainObject, helperList: variableSelector?.variablesListOmsClassification }
                  await ReportActions.getOmsCountReport(omsObject);
                  break;
                case 'notificationsPerDamageCount':
                  console.log('Carregando dashboard Classificação do Incidente')
                  const birdObject = { ...mainObject, helperList: variableSelector?.variablesListDamageLevel }
                  await ReportActions.getBirdGraphReport(birdObject);
                  break;
                case 'notificationsPerUnityCount':
                  console.log('Carregando dashboard por Por unidade')
                  const locationObject = { ...mainObject, helperList: locationSelector?.locationsList }
                  await ReportActions.getPerUnityReport(locationObject);
                  break;
                case 'notificationsPerIncidentSectorCount':
                  console.log('Carregando dashboard por setor notificado')
                  const incidentSector = { ...mainObject, helperList: sectorSelector?.sectorsList }
                  await ReportActions.getPerNotificationSectorReport(incidentSector);             
                  break;
                case 'notificationsPerSectorCount':
                  console.log('Carregando dashboard Por setor notificante')
                  const reportSector = { ...mainObject, helperList: sectorSelector?.sectorsList }
                  await ReportActions.getPerSectorReport(reportSector);
                  break;
                default:
                  console.warn(`Dashboard desconhecido: ${dashboard}`);
                  break;
              }
            }
          }
        } catch (error) {
          console.error("Ocorreu um erro ao carregar Secondary Dashboard:", error);
        }
      }

      if(reloadPrimaryDashboard){
        const mainDashboard = async () => {
          try {
            await ReportActions.getUnityReport(mainObject)
            
            await ReportActions.getSectorsReport(mainObject)
            
            await ReportActions.getStatusReport(mainObject)
            
            await ReportActions.getSubDamageLevelReport(mainObject)

            await ReportActions.getMultiplierStatsReport(mainObject)
            
            await ReportActions.getAutoNotificationReport(mainObject)
            
          } catch (error) {
            console.log("Ocorreu um erro ao carregar unity report.", error)
          }
        }

        // Aguardar mainDashboard e secondaryDashboard terminarem antes de resetar o flag
        Promise.all([mainDashboard(), secondaryDashboard()]).finally(() => {
          setTimeout(() => {
            isDispatchAllRunning.current = false
          }, 100)
        })
      } else {
        // Se não está recarregando o dashboard principal, apenas executar secondaryDashboard
        secondaryDashboard().finally(() => {
          setTimeout(() => {
            isDispatchAllRunning.current = false
          }, 100)
        })
      }
      
      // Removido reset(getValues()) para evitar re-renderizações desnecessárias
    } else {
      isDispatchAllRunning.current = false
    }
  }

  useEffect(() => {
    // Evitar execução no carregamento inicial
    if (isInitialMount.current) {
      isInitialMount.current = false
      setValue('u_dashboards', dashboards)
      return
    }
    
    // Só chamar dispatchAll se dashboards realmente mudou e não está vazio
    if (dashboards && dashboards.length > 0) {
      setValue('u_dashboards', dashboards)
      dispatchAll(false)
    }
  }, [dashboards])


  const selectAll = ({ name, options, isAllSelected }: { name: string, options: any[], isAllSelected: boolean }) => {
    if (isAllSelected) {
      setValue(name, [])
    } else {
      const values = options?.map(option => (option.value))
      setValue(name, values)
    }
  }

  const getFileName = (): string => {
    const date = moment();
    const formattedDate = date.format('DD-MM-YY');
    return `Relatório-Geral-Notifica-${formattedDate}`
  }

  const handleGetBodyForCsv = async () => {
    setLoading(true);

    // Usar o valor do formulário diretamente, com fallback para userLocation
    const selectedLocation = getValues('u_location') || userLocation[0] || null;

    await NotificationActions.getAllToDownload({
      localIncident: selectedLocation,
      sector: userSectors,
      profile: authenticationSelector.dataJwt.profile,
      initialDate: getValues('date_incident')?.toISOString().slice(0, 10),
      finalDate: getValues('date_final')?.toISOString().slice(0, 10),
      dispatch: notificationDispatch,
    }).then(() => {

    }).catch((error) => {
      console.log("GetAllToDownload error", error)
    }).finally(() => {
      setTooltipAlert(true)
      setLoading(false)
    })

  };

  /**
   * Carrega locations e setores com base na seleção do usuário para tela de dashboard
   */
  const onChangeLocation = useCallback((item: string[]) => {
    setValue('u_location', item)
    setUserLocation(item)
    setCanExport(!(item !== null && item !== undefined && item?.length > 0));
    
    // Se não há unidades selecionadas, limpar setores
    if (!item || item.length === 0) {
      setValue('u_sector', [])
      setUserSectors([])
      SectorActions.setSectors(sectorDispatch, [])
      return
    }
    
    const locations = authenticationSelector?.dataJwt?.locations;
    
    if (!locations || !Array.isArray(locations)) {
      return
    }
    
    let allSectors: any[] = [];

    // Primeiro, tentar usar sectorsGroupedByLocationId (mais confiável)
    if (sectorSelector?.sectorsGroupedByLocationId) {
      item.forEach((locationId) => {
        const sectorsForUnity = sectorSelector.sectorsGroupedByLocationId[locationId]
        if (sectorsForUnity && Array.isArray(sectorsForUnity) && sectorsForUnity.length > 0) {
          sectorsForUnity.forEach((sector) => {
            // Evitar duplicados por sys_id
            if (!allSectors.find(s => s.sys_id === sector.sys_id)) {
              allSectors.push({
                u_name: sector.u_name?.trim(),
                sys_id: sector.sys_id,
              })
            }
          })
        }
      })
    }
    
    // Fallback: usar authenticationSelector.dataJwt.locations APENAS para location_manager
    // Seguindo o mesmo padrão da página de Notificações
    if (allSectors.length === 0 && authenticationSelector.dataJwt.profile === 'location_manager') {
      item?.forEach((locationId) => {
        const location = locations.find((loc) => loc.sys_id === locationId);
        
        if (location?.sectors && Array.isArray(location.sectors) && location.sectors.length > 0) {
          location.sectors.forEach((sec: any) => {
            // Evitar duplicados por sys_id
            if (!allSectors.find(s => s.sys_id === sec.sys_id)) {
              allSectors.push({
                u_name: (sec.name || sec.u_name)?.trim(),
                sys_id: sec.sys_id,
              })
            }
          });
        }
      });
    }
    
    // Se ainda não encontrou setores e não é location_manager, tentar buscar do backend
    if (allSectors.length === 0 && authenticationSelector.dataJwt.profile !== 'location_manager') {
      // Buscar setores agrupados por location do backend
      SectorActions.getAllSectorsGroupedByLocationsId(sectorDispatch)
    }
   
    // Atualizar setores no Redux para que sectorMemoSelect seja atualizado
    SectorActions.setSectors(sectorDispatch, allSectors)
    
    // Limpar seleção de setores quando muda a unidade para que o usuário escolha novamente
    setValue('u_sector', [])
    setUserSectors([])

  }, [authenticationSelector?.dataJwt, sectorSelector?.sectorsGroupedByLocationId, setValue, sectorDispatch]);

  /**
   * Carrega locations e setores com base na seleção do usuário para tela de exportação
  */
  const onChangeLocationExport = useCallback((unity: string) => {
      setValue('u_location', unity)
      setUserLocation([unity]) // Atualizar userLocation com o ID selecionado
      setCanExport(!(unity !== null && unity !== undefined && unity !== ""))

      if (authenticationSelector.dataJwt.profile === 'location_manager') {
        let location = authenticationSelector?.dataJwt?.locations.find((loc) => loc.sys_id === unity)
        let sectors = location.sectors.map((sec) => { return { u_name: sec.name, sys_id: sec.sys_id } })
        let sectorsValues = location.sectors.map((sec) => sec.sys_id)

        SectorActions.setSectors(sectorDispatch, sectors)
        setUserSectors(sectorsValues)
      }
    }, [authenticationSelector.dataJwt, sectorDispatch])

  const onChangeSector = useCallback((sectorSelected: string[]) => {
    // Expandir os nomes selecionados para todos os IDs correspondentes
    const expandedSectorIds: string[] = []
    
    sectorSelected.forEach((sectorName) => {
      // Se o mapeamento existe, pegar todos os IDs desse setor
      if (sectorNameToIdsMap[sectorName]) {
        expandedSectorIds.push(...sectorNameToIdsMap[sectorName])
      } else {
        // Fallback: se não estiver no mapeamento, usar o valor diretamente (caso seja um ID)
        expandedSectorIds.push(sectorName)
      }
    })
    
    // Remover duplicados
    const uniqueSectorIds = Array.from(new Set(expandedSectorIds))
    
    setValue('u_sector', sectorSelected) // Manter os nomes no formulário para exibição
    setUserSectors(uniqueSectorIds) // Usar os IDs expandidos para o payload
  }, [sectorNameToIdsMap, setValue]);

  // useEffect para atualizar o mapeamento de nomes para IDs
  useEffect(() => {
    const nameToIdsMap: Record<string, string[]> = {}
    const allSectorsForSelectedLocations: any[] = []
    
    // Se uma ou mais unidades específicas estão selecionadas, filtrar setores por elas
    if (userLocation && userLocation.length > 0) {
      // Primeiro, tentar usar sectorsGroupedByLocationId
      if (sectorSelector?.sectorsGroupedByLocationId) {
        userLocation.forEach((locationId) => {
          const sectorsForUnity = sectorSelector.sectorsGroupedByLocationId[locationId]
          
          if (sectorsForUnity && Array.isArray(sectorsForUnity) && sectorsForUnity.length > 0) {
            sectorsForUnity.forEach((sector) => {
              // Evitar duplicados por sys_id
              if (!allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
                allSectorsForSelectedLocations.push({
                  sys_id: sector.sys_id,
                  u_name: sector.u_name
                })
              }
            })
          }
        })
      }
      
      // Fallback: usar dados de authenticationSelector.dataJwt.locations
      if (allSectorsForSelectedLocations.length === 0 && authenticationSelector?.dataJwt?.locations) {
        const locations = authenticationSelector.dataJwt.locations
        
        userLocation.forEach((locationId) => {
          const location = locations.find((loc) => loc.sys_id === locationId)
          
          if (location?.sectors && Array.isArray(location.sectors) && location.sectors.length > 0) {
            location.sectors.forEach((sector: any) => {
              // Evitar duplicados por sys_id
              if (!allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
                allSectorsForSelectedLocations.push({
                  sys_id: sector.sys_id,
                  u_name: sector.name || (sector as any).u_name
                })
              }
            })
          }
        })
      }
      
      // Agrupar setores por nome (u_name ou name)
      allSectorsForSelectedLocations.forEach((sector: any) => {
        const sectorName = (sector.u_name || sector.name)?.trim()
        if (sectorName && sector.sys_id) {
          if (!nameToIdsMap[sectorName]) {
            nameToIdsMap[sectorName] = []
          }
          if (!nameToIdsMap[sectorName].includes(sector.sys_id)) {
            nameToIdsMap[sectorName].push(sector.sys_id)
          }
        }
      })
    } else {
      // Fallback: agrupar todos os setores por nome
      const allSectors = sectorSelector?.sectorsList || []
      
      allSectors.forEach(sector => {
        const sectorName = sector.u_name?.trim()
        if (sectorName && sector.sys_id) {
          if (!nameToIdsMap[sectorName]) {
            nameToIdsMap[sectorName] = []
          }
          if (!nameToIdsMap[sectorName].includes(sector.sys_id)) {
            nameToIdsMap[sectorName].push(sector.sys_id)
          }
        }
      })
    }
    
    // Atualizar o mapeamento global
    setSectorNameToIdsMap(nameToIdsMap)
  }, [sectorSelector?.sectorsList, sectorSelector?.sectorsGroupedByLocationId, userLocation, authenticationSelector?.dataJwt?.locations])

  const sectorMemoSelect = useMemo(() => {
    const allSectorsForSelectedLocations: any[] = []
    
    // Se uma ou mais unidades específicas estão selecionadas, filtrar setores por elas
    if (userLocation && userLocation.length > 0) {
      // Primeiro, tentar usar sectorsGroupedByLocationId
      if (sectorSelector?.sectorsGroupedByLocationId) {
        userLocation.forEach((locationId) => {
          const sectorsForUnity = sectorSelector.sectorsGroupedByLocationId[locationId]
          
          if (sectorsForUnity && Array.isArray(sectorsForUnity) && sectorsForUnity.length > 0) {
            sectorsForUnity.forEach((sector) => {
              // Evitar duplicados por sys_id
              if (!allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
                allSectorsForSelectedLocations.push({
                  sys_id: sector.sys_id,
                  u_name: sector.u_name
                })
              }
            })
          }
        })
      }
      
      // Fallback 1: usar dados de authenticationSelector.dataJwt.locations
      if (allSectorsForSelectedLocations.length === 0 && authenticationSelector?.dataJwt?.locations) {
        const locations = authenticationSelector.dataJwt.locations
        
        userLocation.forEach((locationId) => {
          const location = locations.find((loc) => loc.sys_id === locationId)
          
          if (location?.sectors && Array.isArray(location.sectors) && location.sectors.length > 0) {
            location.sectors.forEach((sector: any) => {
              // Evitar duplicados por sys_id
              if (!allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
                allSectorsForSelectedLocations.push({
                  sys_id: sector.sys_id,
                  u_name: sector.name || sector.u_name
                })
              }
            })
          }
        })
      }
      
      // Fallback 2: usar sectorSelector.sectorsList (atualizado pelo onChangeLocation)
      if (allSectorsForSelectedLocations.length === 0 && sectorSelector?.sectorsList && sectorSelector.sectorsList.length > 0) {
        // Filtrar setores que pertencem às unidades selecionadas
        const locations = authenticationSelector?.dataJwt?.locations || []
        const locationSysIds = new Set(userLocation)
        
        sectorSelector.sectorsList.forEach((sector: any) => {
          // Verificar se o setor pertence a alguma das unidades selecionadas
          const belongsToSelectedLocation = locations.some((loc: any) => 
            locationSysIds.has(loc.sys_id) && 
            loc.sectors?.some((sec: any) => sec.sys_id === sector.sys_id)
          )
          
          if (belongsToSelectedLocation && !allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
            allSectorsForSelectedLocations.push({
              sys_id: sector.sys_id,
              u_name: sector.u_name
            })
          }
        })
        
        // Se ainda não encontrou, usar todos os setores (pode ser que não tenhamos a relação location-sector)
        if (allSectorsForSelectedLocations.length === 0) {
          sectorSelector.sectorsList.forEach((sector: any) => {
            if (!allSectorsForSelectedLocations.find(s => s.sys_id === sector.sys_id)) {
              allSectorsForSelectedLocations.push({
                sys_id: sector.sys_id,
                u_name: sector.u_name
              })
            }
          })
        }
      }
      
      if (allSectorsForSelectedLocations.length > 0) {
        // Agrupar setores por nome (u_name) para exibição
        const uniqueSectorNames = new Set<string>()
        allSectorsForSelectedLocations.forEach((sector: any) => {
          const sectorName = (sector.u_name || sector.name)?.trim()
          if (sectorName) {
            uniqueSectorNames.add(sectorName)
          }
        })
        
        const result = Array.from(uniqueSectorNames)
          .map((sectorName) => ({
            value: sectorName, // Usar o nome como value para identificar o grupo
            label: sectorName
          }))
          .filter(sec => sec.value && sec.label)
          .sort((o1, o2) => o1.label?.localeCompare(o2.label))
        
        return result
      }
    }
    
    // Fallback: mostrar todos os setores agrupados por nome
    const allSectors = sectorSelector?.sectorsList || []
    const uniqueSectorNames = new Set<string>()
    
    allSectors.forEach(sector => {
      const sectorName = sector.u_name?.trim()
      if (sectorName) {
        uniqueSectorNames.add(sectorName)
      }
    })
    
    // Retornar apenas um item por nome
    return Array.from(uniqueSectorNames)
      .map((sectorName) => ({
        value: sectorName,
        label: sectorName
      }))
      .filter(sec => sec.value && sec.label)
      .sort((o1, o2) => o1.label?.localeCompare(o2.label))
  }, [sectorSelector?.sectorsList, sectorSelector?.sectorsGroupedByLocationId, userLocation, authenticationSelector?.dataJwt?.locations])

  useEffect(() => {
    const csv = notificationSelector.notificationsCsvReport
    if (csv !== '') {
      const blob = new Blob([csv], { type: 'text/csv' })
      const fileName = getFileName()
      const url = window.URL?.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      notificationDispatch({ type: REDUX.reducers.CLEAN_CSV_DATA, } as NotificationAction)
    }
  }, [notificationSelector.notificationsCsvReport])

  const omsMemoSelect = useMemo(() => (
    variableSelector?.variablesListOmsClassification
      ?.slice(1)
      ?.map((l) => ({
        value: l?.value,
        label: l?.label
      }))
      .sort((o1, o2) =>
        o1.label?.localeCompare(o2.label)
      )
  ), [variableSelector?.variablesListOmsClassification])

  const tipoMemoSelect = useMemo(() => (
    variableSelector.variablesListIncidentType?.slice(1)
      ?.map((l) => ({
        value: l?.value,
        label: l?.label
      }))
  ), [variableSelector?.variablesListIncidentType])

  const damageMemoSelect = useMemo(() => (
    variableSelector.variablesListDamageLevel
      ?.slice(1)
      ?.map((l) => ({
        value: l?.value,
        label: l?.label
      }))
  ), [variableSelector.variablesListDamageLevel])

  useEffect(() => {
    if (damageMemoSelect?.length > 0) {
      setValue(
        'damage_level',
        damageMemoSelect
          .filter(o => o.value !== DAMAGE_LEVEL_LIST.notifiable.id)
          .map(o => o.value)
      )
    }
  }, [damageMemoSelect, setValue])

  const locationsProfile = useMemo(() => {
    const profile = authenticationSelector?.dataJwt?.profile;
    if (['nsp', 'location_qa', 'unit_general_manager', 'location_manager'].includes(profile)) {
      const userLocations = authenticationSelector?.dataJwt?.locations || [];
      return userLocations;
    }
    return locationSelector.locationsList;
  }, [authenticationSelector?.dataJwt]);

  const locationMemoSelect = useMemo(() => (
    locationsProfile?.map((l) => ({
      value: l.sys_id,
      label: l.name
    }))
      .sort((o1, o2) =>
        o1.label.localeCompare(o2.label)
      )
  ), [locationsProfile])

  const sentinelEventMemoSelect = useMemo(() => (
    notificationSelector?.sentinelEvent
      ?.map((l) => ({
        value: l.sys_id,
        label: l.description
      }))
      .sort((o1, o2) =>
        o1.label.localeCompare(o2.label)
      )
  ), [notificationSelector?.sentinelEvent])


  const dashboardItens = [ 
    {
      name: "notificationsPerMonthCount",
      key: "1",
      label: "Total notificações"
    },
    {
      name: "mostReclassifiedIncidentType",
      key: "2",
      label: "Top 10 incidentes"
    },
    {
      name: "notificationsPerOmsCount",
      key: "3",
      label: "Classificação OMS"
    },
    {
      name: "notificationsPerDamageCount",
      key: "4",
      label: "Classificação do Incidente"
    },
    {
      name: "notificationsPerUnityCount",
      key: "5",
      label: "Por unidade"
    },
    {
      name: "notificationsPerIncidentSectorCount",
      key: "6",
      label: "Por setor notificado"
    },
    {
      name: "notificationsPerSectorCount",
      key: "7",
      label: "Por setor notificante"
    }
  ];

  const handleCheckboxDashboardsChange = (item) => {
    setDashboards((e) => {
      if (e.includes(item)) {
        return e.filter((dashboard) => dashboard !== item);
      }     
      return [...e, item];
    });
  };

  const handleFilterClick = () => {
    setDashboards([]);
    dispatchAll();
  };

  return (
    <FilterMainContainer data-testid="test-relatorio-filters">
      <FilterTitle>
        Filtros
      </FilterTitle>
      <HrCustomLine />
      <FilterSection>
        {pathname ===
          ROUTES_NAMES.reports &&
          <>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                multiUnitText="Unidades"
                unitText="Unidade"
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[360px]"
                styleContainer="w-[170px]"
                label="Unidade"
                name="u_location"
                control={control}
                data={locationMemoSelect}
                onChange={onChangeLocation}
                placeholder="Todas"
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                multiUnitText="Setores"
                unitText="Setor"
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[300px]"
                styleContainer="w-[170px]"
                label="Setor notificado"
                name="u_sector"
                control={control}
                data={sectorMemoSelect}
                onChange={onChangeSector}
                placeholder="Todas"
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                required={true}
                styleBoxOptions="mt-3 w-[360px]"
                styleContainer="w-[170px]"
                label="Status Setor"
                name="sectorActive"
                control={control}
                data={['todos','false', 'true'].map((e) => ({
                  value: e === 'todos' ? null :  (e === 'true' ? 'Ativo' : 'Inativo'),
                  label: e === 'todos' ? 'Todos' : (e === 'true' ? 'Ativo' : 'Inativo')
                }))}
                defaultValue={null}
                placeholder='Todos'
              />
            </div>
            <div className='flex flex-col gap-1 mb-2'>
              <CustomDatePicker
                name="date_incident"
                control={control}
                required
                label="Data incidente (início e fim)"
                placeholderData="Selecionar"
                dateValue={moment().subtract(31, 'days').toDate()}
                maxDate={maxDate}
              />
              <CustomDatePicker
                name="date_final"
                control={control}
                required
                ptIcon
                placeholderData="Selecionar"
                dateValue={new Date()}
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                multiUnitText="Classificações dos Incidentes"
                unitText="Classificação do Incidente"
                selectAll={selectAll}
                allIsSelected={false}
                hideSelectAll={true}
                multiple={true} 
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[300px]"
                styleContainer="w-[170px]"
                label="Classificação do Incidente"
                name="damage_level"
                control={control}
                data={damageMemoSelect}
                placeholder="Todas"
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                required={true}
                styleBoxOptions="mt-3 w-[360px]"
                styleContainer="w-[170px]"
                label="Aberto por"
                name="u_user_type"
                control={control}
                data={[
                  { value: 'u_patient', label: 'Paciente' },
                  { value: 'u_employee', label: 'Administrativo' }
                ]}
                defaultValue={null}
                placeholder='Todos'
                onChange={(value) => {
                  // Apenas atualiza o valor, não dispara chamadas automáticas
                  setValue('u_user_type', value)
                }}
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                unitText="Tipo"
                multiUnitText="Tipos"
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                label="Tipo do incidente"
                styleBoxOptions="mt-3 w-[460px]"
                styleContainer="w-[170px]"
                name="incident_type"
                control={control}
                data={tipoMemoSelect}
                placeholder="Todas"
                required
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                multiUnitText="Classificações"
                unitText="Classificação"
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[400px]"
                styleContainer="w-[170px]"
                label="Classificação da OMS"
                name="u_oms_classification"
                control={control}
                data={omsMemoSelect}
                placeholder="Todas"
              />
            </div>
            <div className='mb-2'>
              <InputSelect
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[400px]"
                styleContainer="w-[170px]"
                label="Status"
                name="u_state"
                control={control}
                data={notificationStatus}
                placeholder="Todas"
                unitText="Status"
                multiUnitText="Status"
              />
            </div>
            {/* <div className='mb-2'>
          <InputSelect
            multiUnitText="Eventos"
            unitText="Evento"
            selectAll={selectAll}
            allIsSelected={true}
            multiple={true}
            shouldHaveSearch={true}
            styleBoxOptions="mt-3 w-[400px]"
            styleContainer="w-[170px]"
            label="Evento Sentinela"
            name="sentinel_event"
            control={control}
            data={sentinelEventMemoSelect}
            placeholder="Todos"
          />
        </div> */}
            <div className='mb-2'>
              <InputSelect
                multiUnitText="Riscos"
                unitText="Risco"
                selectAll={selectAll}
                allIsSelected={true}
                multiple={true}
                shouldHaveSearch={true}
                styleBoxOptions="mt-3 w-[400px]"
                styleContainer="w-[170px]"
                label="Risco de Imagem"
                name="image_risk"
                control={control}
                data={imageRisk}
                placeholder="Todas"
              />
            </div>
            {/* <div className='mb-2'>
              <InputSelect
                required={true}
                styleBoxOptions="mt-3 w-[360px]"
                styleContainer="w-[170px]"
                label="Notificação em massa"
                name="multipleType"
                control={control}
                searchPlaceHolder="Buscar"
                data={multipleNotification.map((l) => ({
                  value: l.id,
                  label: l.title
                }))}
                defaultValue={multipleNotification[0].id}
              />

            </div>  */}
            <FilterButton onClick={handleFilterClick}
              className={`${false ? 'bg-disabled border-textSelect text-textSelect' : ''}`}
            >
              Filtrar
            </FilterButton>
            
           <div className='mb-2'>           
            <FilterTitle>
              Selecione os gráficos
            </FilterTitle>

            <HrCustomLine  className='mt-2 mb-3'/>

              <div className='flex flex-col gap-2 mt-2'>
                {dashboardItens?.map(item => (
                  <label key={item?.key} className='flex items-center gap-2'>
                    <CheckboxList
                      control={control}
                      id={item?.key}
                      name={item?.name}
                      checked={dashboards?.includes(item.name)}
                      style={{width: '18px', height: '18px', border: '1.5px solid rgba(189, 189, 189, 1)', borderRadius: '4px'}}
                      onChange={() => handleCheckboxDashboardsChange(item?.name)}
                    />
                    <span className="font-normal text-[14px]">
                        {item?.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

          </>
        }
        {pathname ===
          ROUTES_NAMES.exportReport &&
          <>
            <div className='mb-2'>
              <InputSelect
                searchPlaceHolder="Buscar"
                shouldHaveSearch={true}
                unitText="Unidade"
                styleBoxOptions="mt-3 w-[360px]"
                styleContainer="w-[170px]"
                label="Unidade"
                name="u_location"
                control={control}
                data={locationMemoSelect}
                placeholder="Selecionar"
                onChange={onChangeLocationExport}
              />
            </div>
            <div className='flex flex-col gap-1 mb-2'>
              <CustomDatePicker
                name="date_incident"
                control={control}
                required
                label="Data incidente (início e fim)"
                placeholderData="Selecionar"
                dateValue={moment().subtract(31, 'days').toDate()}
                maxDate={maxDate}
              />
              <CustomDatePicker
                name="date_final"
                control={control}
                required
                ptIcon
                placeholderData="Selecionar"
                dateValue={new Date()}
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
            <div className='my-4'>
              <ExportButton
                data-testid="test-csv-link"
                type='button'
                onClick={handleGetBodyForCsv}
                disabled={canExport}
                className={`p-2 ${canExport ? disabledExport : 'hover:brightness-90'}`}
              >
                <AiOutlineDownload size={20} />
                Exportar relatório
              </ExportButton>
            </div>
          </>
        }
      </FilterSection>
    </FilterMainContainer>
  )
}

export { LeftSideFilters }